import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link, Navigate, useNavigate } from 'react-router-dom';
import Registration from './components/Registration.jsx';
import Login from './components/Login.jsx';
import PhotoUpload from './components/PhotoUpload.jsx';
import Dashboard from './components/Dashboard.jsx';
import AdminPanel from './components/AdminPanel.jsx';
import AdminLogin from './components/AdminLogin.jsx';
import CaptureShell from './capture/CaptureShell.jsx';
import './App.css';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isUserPage = location.pathname.startsWith('/upload/') || location.pathname.startsWith('/dashboard/');
  const isAdminPage = location.pathname === '/admin';
  const userName = sessionStorage.getItem('userName');
  const adminToken = sessionStorage.getItem('adminToken');

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleUserLogout = () => {
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('userName');
    setMenuOpen(false);
    navigate('/login');
  };

  const handleAdminLogout = () => {
    sessionStorage.removeItem('adminToken');
    setMenuOpen(false);
    navigate('/admin/login');
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (userName) {
      navigate(`/dashboard/${encodeURIComponent(userName)}`);
    } else {
      navigate('/register');
    }
  };

  const userInitial = userName ? userName.trim().charAt(0).toUpperCase() : 'U';

  return (
    <header className="App-header">
      <div className="header-left">
        <button onClick={handleLogoClick} className="header-logo" title="Imaginary Home">
          <img src="/logo192.png" alt="Imaginary Logo" className="brand-logo-img" />
          <div className="brand-text-col">
            <span className="brand-title">Imaginary</span>
          </div>
        </button>
      </div>

      <div className="header-right" ref={menuRef}>
        {isUserPage || isAdminPage || userName ? (
          <div className="avatar-menu-wrapper">
            <button
              type="button"
              className="avatar-profile-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="User Profile Menu"
              aria-expanded={menuOpen}
            >
              <div className="avatar-circle">
                {isAdminPage ? '🛡️' : userInitial}
              </div>
              <span className="avatar-name-label">
                {isAdminPage ? 'Admin' : (userName || 'Account')}
              </span>
              <span className="avatar-chevron">{menuOpen ? '▴' : '▾'}</span>
            </button>

            {menuOpen && (
              <div className="profile-dropdown-card">
                <div className="dropdown-user-header">
                  <div className="dropdown-user-initial">
                    {isAdminPage ? '🛡️' : userInitial}
                  </div>
                  <div className="dropdown-user-info">
                    <strong>{isAdminPage ? 'Administrator' : (userName || 'Verified User')}</strong>
                    <span className="dropdown-user-tag">{isAdminPage ? 'System Admin' : 'Participant'}</span>
                  </div>
                </div>

                <div className="dropdown-divider" />

                <div className="dropdown-links">
                  {userName && (
                    <>
                      <Link
                        to={`/upload/${encodeURIComponent(userName)}`}
                        className={`dropdown-link-item ${location.pathname.startsWith('/upload/') ? 'active' : ''}`}
                      >
                        📸 Verification Capture
                      </Link>
                      <Link
                        to={`/dashboard/${encodeURIComponent(userName)}`}
                        className={`dropdown-link-item ${location.pathname.startsWith('/dashboard/') ? 'active' : ''}`}
                      >
                        📊 Progress Dashboard
                      </Link>
                    </>
                  )}

                  {/* Admin link discreetly inside menu */}
                  {(adminToken || isAdminPage) ? (
                    <Link
                      to="/admin"
                      className={`dropdown-link-item ${isAdminPage ? 'active' : ''}`}
                    >
                      🛡️ Admin Control Panel
                    </Link>
                  ) : (
                    <Link
                      to="/admin/login"
                      className="dropdown-link-item text-muted"
                    >
                      🔒 Staff / Admin Sign In
                    </Link>
                  )}
                </div>

                <div className="dropdown-divider" />

                <div className="dropdown-footer">
                  {isAdminPage ? (
                    <button onClick={handleAdminLogout} className="dropdown-logout-btn">
                      🚪 Logout Admin
                    </button>
                  ) : (
                    <button onClick={handleUserLogout} className="dropdown-logout-btn">
                      🚪 Log Out
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="header-auth-pills">
            {location.pathname !== '/login' && (
              <Link to="/login" className="header-auth-link login-pill">
                Sign In
              </Link>
            )}
            {location.pathname !== '/register' && (
              <Link to="/register" className="header-auth-link register-pill">
                Register
              </Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

const AppContent = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <div className="App">
      <Header />
      <main className={isAdmin ? 'admin-main-wrapper' : 'user-main-wrapper'}>
        <Routes>
          <Route path="/" element={<Navigate to="/register" replace />} />
          <Route path="/register" element={<Registration />} />
          <Route path="/login" element={<Login />} />
          <Route path="/upload/:userName" element={<PhotoUpload />} />
          <Route path="/dashboard/:userName" element={<Dashboard />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/capture" element={<CaptureShell />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
